import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminShell } from './components/layout/AdminShell.js';
import { CampaignConsole } from './components/campaigns/CampaignConsole.js';
import { ConversationList } from './components/conversations/ConversationList.js';
import { InfluencerImportConsole } from './components/influencer-imports/InfluencerImportConsole.js';
import { ChatPanel } from './components/chat/ChatPanel.js';
import { ProfilePanel } from './components/profile/ProfilePanel.js';
import { EnvironmentSettings } from './components/settings/EnvironmentSettings.js';
import { GroupConversationConsole } from './components/groups/GroupConversationConsole.js';
import { AnalysisConsole } from './components/analysis/AnalysisConsole.js';
import { DashboardConsole } from './components/dashboard/DashboardConsole.js';
import { LoginForm } from './components/auth/LoginForm.js';
import { UsageConsole } from './components/usage/UsageConsole.js';
import { OnboardingConsole } from './components/onboarding/OnboardingConsole.js';
import { useActiveConversation } from './hooks/useActiveConversation.js';
import { useAuth } from './hooks/useAuth.js';
import { useConversations } from './hooks/useConversations.js';
import { useToast } from './hooks/useToast.js';
import { resolveProfileMemoryWarnings, runProfileRefinery } from './api/adminApi.js';
import type { AdminConversation, ProfileRefineryMode } from './api/types.js';
import './styles/chat.css';
import './styles/feedback.css';
import './styles/profile.css';
import './styles/settings.css';
import './styles/showroom.css';

type AdminModule = 'home' | 'chat' | 'groups' | 'analysis' | 'dashboard' | 'campaigns' | 'influencer-imports' | 'usage' | 'onboarding';

/** 演示视角人设：按登录账号取老板/业务员姓名，未列出的账号沿用七兔演示默认（方毅/阿柔）。 */
const ROLE_PERSONAS: Record<string, { boss: string; staff: string }> = {
  sanchuan: { boss: '黄雪梅', staff: '三川' },
};

/** 根路径 /admin/ 是角色落地页（admin → 老板看板，sales → 今日名单）；会话页在 /admin/chat。 */
function readAdminModule(): AdminModule {
  const path = window.location.pathname.replace(/\/+$/u, '');
  if (path === '/admin' || path === '/admin-dev') return 'home';
  if (window.location.pathname.startsWith('/admin/dashboard') || window.location.pathname.startsWith('/admin-dev/dashboard')) return 'dashboard';
  if (window.location.pathname.startsWith('/admin/usage') || window.location.pathname.startsWith('/admin-dev/usage')) return 'usage';
  if (window.location.pathname.startsWith('/admin/onboarding') || window.location.pathname.startsWith('/admin-dev/onboarding')) return 'onboarding';
  if (window.location.pathname.startsWith('/admin/groups') || window.location.pathname.startsWith('/admin-dev/groups')) {
    return 'groups';
  }
  if (window.location.pathname.startsWith('/admin/analysis') || window.location.pathname.startsWith('/admin-dev/analysis')) {
    return 'analysis';
  }
  if (
    window.location.pathname.startsWith('/admin/influencer-imports')
    || window.location.pathname.startsWith('/admin-dev/influencer-imports')
  ) {
    return 'influencer-imports';
  }
  return window.location.pathname.startsWith('/admin/campaigns')
    || window.location.pathname.startsWith('/admin-dev/campaigns')
    ? 'campaigns'
    : 'chat';
}

function modulePath(module: AdminModule): string {
  const base = window.location.pathname.startsWith('/admin-dev') ? '/admin-dev' : '/admin';
  if (module === 'campaigns') return `${base}/campaigns`;
  if (module === 'groups') return `${base}/groups`;
  if (module === 'analysis') return `${base}/analysis`;
  if (module === 'dashboard') return `${base}/dashboard`;
  if (module === 'influencer-imports') return `${base}/influencer-imports`;
  if (module === 'usage') return `${base}/usage`;
  if (module === 'onboarding') return `${base}/onboarding`;
  if (module === 'chat') return `${base}/chat`;
  return `${base}/`;
}

function App() {
  const auth = useAuth();
  const previousUserIdRef = useRef<number | undefined>(undefined);
  const [activeModule, setActiveModule] = useState<AdminModule>(() => readAdminModule());
  const [activeFriendId, setActiveFriendId] = useState<number | null>(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [viewAsStaff, setViewAsStaff] = useState<boolean | null>(null);
  const [analysisEntryKey, setAnalysisEntryKey] = useState(0);
  // 仅群聊按钮写入，首次详情读取后消费；刷新、分享和历史导航不携带运行意图。
  const analysisRunIntent = useRef<number | null>(null);
  const { toasts, showToast } = useToast();
  const {
    conversations,
    loading: conversationsLoading,
    error: conversationsError,
    searchTerm,
    debouncedSearchTerm,
    scope,
    pagination,
    setSearchTerm,
    setScope,
    goToPreviousPage,
    goToNextPage,
    refresh: refreshConversations,
    upsertConversation,
    clear: clearConversations,
  } = useConversations({ enabled: auth.authenticated && auth.user?.role !== 'customer' });

  const {
    messages,
    conversationAiCost,
    hasMoreMessages,
    loading: messagesLoading,
    error: messagesError,
    messageFeedback,
    conversationFeedback,
    quality,
    sending,
    loadMessages,
    loadOlderMessages,
    loadConversationFeedback,
    setMode,
    handleSendReply,
    handleRunQualityCheck,
    handleSaveMessageFeedback,
    handleSaveConversationFeedback,
    clear,
  } = useActiveConversation();

  const navigateModule = useCallback((module: AdminModule) => {
    analysisRunIntent.current = null;
    if (module === 'analysis' || module === 'groups') setAnalysisEntryKey((key) => key + 1);
    setActiveModule(module);
    window.history.pushState({}, '', modulePath(module));
    setMobileListOpen(false);
    setMobileProfileOpen(false);
  }, []);

  const openAnalysisHome = (asStaff: boolean) => {
    analysisRunIntent.current = null;
    setViewAsStaff(asStaff);
    const view = asStaff ? 'today' : 'all';
    window.history.pushState({}, '', `${modulePath('analysis')}?view=${view}`);
    setActiveModule('analysis');
    // Re-entering the same page must reset its list, filters and selected detail.
    setAnalysisEntryKey((key) => key + 1);
  };

  const activeConversation = useMemo(
    () => conversations.find((c) => c.friendId === activeFriendId) || null,
    [conversations, activeFriendId],
  );

  const handleSelectConversation = useCallback(
    async (friendId: number) => {
      setActiveFriendId(friendId);
      setMobileListOpen(false);
      setMobileProfileOpen(false);
      const selected = conversations.find((conversation) => conversation.friendId === friendId);
      await loadMessages(friendId);
      if (selected) {
        await loadConversationFeedback(selected.id);
      }
    },
    [conversations, loadConversationFeedback, loadMessages],
  );

  const handleRefreshActiveConversation = useCallback(
    async (conversation: AdminConversation) => {
      await Promise.all([
        loadMessages(conversation.friendId),
        loadConversationFeedback(conversation.id),
        refreshConversations({ background: true }),
      ]);
      showToast('聊天记录已刷新', 'success');
    },
    [loadConversationFeedback, loadMessages, refreshConversations, showToast],
  );

  const handleModeToggle = useCallback(
    async (conversation: AdminConversation) => {
      const nextMode = conversation.mode === 'manual' ? 'auto' : 'manual';
      const updated = await setMode(conversation.friendId, nextMode);
      if (updated) {
        upsertConversation(updated);
        showToast(nextMode === 'manual' ? '已切为人工模式' : '已切回自动模式', 'success');
      }
    },
    [setMode, upsertConversation, showToast],
  );

  const handleQualityCheck = useCallback(
    async (friendId: number) => {
      await handleRunQualityCheck(friendId);
      showToast('质检完成', 'success');
    },
    [handleRunQualityCheck, showToast],
  );

  const handleSaveMessageFeedbackWrapped = useCallback(
    async (messageId: number, input: Parameters<typeof handleSaveMessageFeedback>[1]) => {
      await handleSaveMessageFeedback(messageId, input);
      showToast('反馈已保存', 'success');
    },
    [handleSaveMessageFeedback, showToast],
  );

  const handleSaveConversationFeedbackWrapped = useCallback(
    async (conversationId: number, summary: string) => {
      await handleSaveConversationFeedback(conversationId, summary);
      showToast('会话评语已保存', 'success');
    },
    [handleSaveConversationFeedback, showToast],
  );

  const handleRunProfileRefinery = useCallback(
    async (friendId: number, mode: ProfileRefineryMode) => {
      await runProfileRefinery(friendId, { mode });
      await refreshConversations({ background: true });
      showToast(mode === 'accept' ? '档案卡验收完成' : '档案卡整理完成', 'success');
    },
    [refreshConversations, showToast],
  );

  const handleResolveProfileMemoryWarnings = useCallback(
    async (friendId: number) => {
      await resolveProfileMemoryWarnings(friendId);
      await refreshConversations({ background: true });
      showToast('档案 warning 已标记处理', 'success');
    },
    [refreshConversations, showToast],
  );

  useEffect(() => {
    if (conversationsError) showToast(conversationsError, 'error');
  }, [conversationsError, showToast]);

  useEffect(() => {
    if (messagesError) showToast(messagesError, 'error');
  }, [messagesError, showToast]);

  useEffect(() => {
    if (activeFriendId && !activeConversation) {
      clear();
      setActiveFriendId(null);
    }
  }, [activeFriendId, activeConversation, clear]);

  useEffect(() => {
    if (activeConversation) {
      loadConversationFeedback(activeConversation.id);
    }
  }, [activeConversation?.id, loadConversationFeedback]);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const nextUserId = auth.user?.id;
    previousUserIdRef.current = nextUserId;
    if (!previousUserId && nextUserId) return;
    if (previousUserId === nextUserId) return;
    clear();
    clearConversations();
    setActiveFriendId(null);
    setMobileListOpen(false);
    setMobileProfileOpen(false);
    setSettingsOpen(false);
  }, [auth.user?.id, clear, clearConversations]);

  useEffect(() => {
    if (!auth.authenticated) return undefined;
    const timer = window.setInterval(() => {
      void refreshConversations({ background: true });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [auth.authenticated, refreshConversations]);

  useEffect(() => {
    const handlePopState = () => {
      analysisRunIntent.current = null;
      setAnalysisEntryKey((key) => key + 1);
      setActiveModule(readAdminModule());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 达人导入只对 admin 开放；老板看板 admin 与 sales 都可看（sales 金额由后端脱敏、只见绑定工作号）。
  useEffect(() => {
    if (!auth.authenticated || auth.user?.role === 'admin') return;
    if (activeModule === 'influencer-imports') navigateModule('chat');
  }, [activeModule, auth.authenticated, auth.user?.role, navigateModule]);

  // 角色首页分流：admin 落老板看板，sales 落今日名单（智能分析 ?view=today），customer 落用量。
  useEffect(() => {
    if (!auth.authenticated || activeModule !== 'home') return;
    const role = auth.user?.role;
    if (role === 'admin') {
      navigateModule('dashboard');
    } else if (role === 'sales') {
      const base = window.location.pathname.startsWith('/admin-dev') ? '/admin-dev' : '/admin';
      window.history.replaceState({}, '', `${base}/analysis?view=today`);
      setActiveModule('analysis');
    } else {
      navigateModule('usage');
    }
  }, [activeModule, auth.authenticated, auth.user?.role, navigateModule]);

  useEffect(() => {
    if (auth.authenticated && auth.user?.role === 'customer' && activeModule !== 'usage') navigateModule('usage');
  }, [activeModule, auth.authenticated, auth.user?.role, navigateModule]);

  const unreadCount = useMemo(
    () => conversations.reduce((sum, item) => sum + item.unreadCount, 0),
    [conversations],
  );

  const scopeLabel = scope === 'whitelist' ? '白名单' : scope === 'manual' ? '已转人工' : '全部';
  const statText = `${scopeLabel} 当前页 ${conversations.length} 个 · ${unreadCount} 条未读`;

  if (auth.loading) {
    return <main className="auth-screen"><div className="auth-loading">加载中...</div></main>;
  }

  if (!auth.authenticated || !auth.user) {
    return <LoginForm error={auth.error} onSubmit={auth.login} />;
  }

  if (activeModule === 'home') {
    return <main className="auth-screen"><div className="auth-loading">正在进入…</div></main>;
  }

  const displayName = auth.user.displayName || auth.user.username;
  const isCustomer = auth.user.role === 'customer';
  const isAdmin = auth.user.role === 'admin';
  const staffView = viewAsStaff ?? auth.user.role === 'sales';
  // 演示视角人设按账号区分：sanchuan（三川商家）为黄雪梅/三川，其余沿用默认方毅/阿柔。
  const { boss: bossName, staff: staffName } = ROLE_PERSONAS[auth.user.username] ?? { boss: '方毅', staff: '阿柔' };
  const roleText = isAdmin ? '老板 · admin' : isCustomer ? '客户' : '业务员 · sales';

  return (
    <>
      <div className="app-frame">
        <aside className="sb">
          <div className="who">
            <div className="ava">{displayName.slice(0, 1)}</div>
            <b>{displayName}</b>
            <span className="who-role">{roleText}</span>
            <button type="button" className="who-settings" title="环境设置" onClick={() => setSettingsOpen(true)}>⚙︎</button>
          </div>
          {!isCustomer ? (
            <button type="button" className="search" onClick={() => navigateModule('chat')}>🔍 搜索会话</button>
          ) : null}
          <div className="glabel">导航</div>
          {!isCustomer ? (
            <button
              type="button"
              className={`item${activeModule === 'groups' ? ' on' : ''}`}
              onClick={() => navigateModule('groups')}
            >
              🗂 群聊
            </button>
          ) : null}
          <button
            type="button"
            className={`item${activeModule === 'usage' ? ' on' : ''}`}
            onClick={() => navigateModule('usage')}
          >
            📊 用量
          </button>
          {isAdmin ? (
            <button
              type="button"
              className={`item${activeModule === 'influencer-imports' ? ' on' : ''}`}
              onClick={() => navigateModule('influencer-imports')}
            >
              🧬 达人导入
            </button>
          ) : null}
          {!isCustomer ? (
            <>
              <div className="glabel" style={{ marginTop: 16 }}>已打开产品</div>
              <button type="button" className="item on">💬 私域 AI</button>
            </>
          ) : null}
          <div className="foot">
            知行猿 · 私域 AI 销售大脑
            <button type="button" onClick={() => void auth.logout()}>退出登录</button>
          </div>
        </aside>
        <div className="main">
          {!isCustomer ? (
            <div className="tabs">
              <button
                type="button"
                className={activeModule === 'dashboard' ? 'on' : ''}
                onClick={() => navigateModule('dashboard')}
              >
                老板看板
              </button>
              <button
                type="button"
                className={activeModule === 'analysis' ? 'on' : ''}
                onClick={() => navigateModule('analysis')}
              >
                客户智能分析
              </button>
              <button
                type="button"
                className={activeModule === 'chat' ? 'on' : ''}
                onClick={() => navigateModule('chat')}
              >
                实时对话
                {unreadCount > 0 ? <span className="tabs-dot" /> : null}
              </button>
              <button
                type="button"
                className={activeModule === 'campaigns' ? 'on' : ''}
                onClick={() => navigateModule('campaigns')}
              >
                老客激活
              </button>
              {isAdmin ? (
                <button
                  type="button"
                  className={activeModule === 'onboarding' ? 'on' : ''}
                  onClick={() => navigateModule('onboarding')}
                >
                  搭建向导
                </button>
              ) : null}
              <span className="role-switch" aria-label="页面视角">
                <button type="button" className={!staffView ? 'on' : ''} aria-pressed={!staffView} onClick={() => openAnalysisHome(false)}>👑 {bossName} · 老板</button>
                <button type="button" className={staffView ? 'on' : ''} aria-pressed={staffView} onClick={() => openAnalysisHome(true)}>🧑‍💼 {staffName} · 业务员</button>
              </span>
            </div>
          ) : null}
          {activeModule === 'usage' ? (
        <UsageConsole user={auth.user} onToast={showToast} />
      ) : activeModule === 'onboarding' && auth.user.role === 'admin' ? (
        <OnboardingConsole onToast={showToast} />
      ) : activeModule === 'campaigns' ? (
        <CampaignConsole
          user={auth.user}
          onToast={showToast}
          onBackToChat={() => navigateModule('chat')}
        />
      ) : activeModule === 'groups' ? (
        <GroupConversationConsole key={analysisEntryKey} onToast={showToast} onOpenAnalysis={(id) => {
          analysisRunIntent.current = id;
          window.history.pushState({}, '', `${modulePath('analysis')}?conversationId=${id}`);
          setAnalysisEntryKey((key) => key + 1);
          setActiveModule('analysis');
        }} />
      ) : activeModule === 'dashboard' && !isCustomer ? (
        <DashboardConsole
          isAdmin={auth.user.role === 'admin'}
          onToast={showToast}
          onOpenView={(view, params) => {
            const base = window.location.pathname.startsWith('/admin-dev') ? '/admin-dev' : '/admin';
            const search = new URLSearchParams(params ?? {});
            if (view !== 'all') search.set('view', view);
            const query = search.toString();
            window.history.pushState({}, '', `${base}/analysis${query ? `?${query}` : ''}`);
            setActiveModule('analysis');
          }}
        />
      ) : activeModule === 'analysis' ? (
        <AnalysisConsole
          key={analysisEntryKey}
          staffName={staffName}
          consumeRunIntent={(id) => {
            const matches = analysisRunIntent.current === id;
            analysisRunIntent.current = null;
            return matches;
          }}
          isAdmin={auth.user.role === 'admin' && !staffView}
          onToast={showToast}
          onOpenGroup={(row) => {
            const base = window.location.pathname.startsWith('/admin-dev') ? '/admin-dev' : '/admin';
            analysisRunIntent.current = null;
            window.history.pushState(null, '', `${base}/groups?conversationId=${row.conversationId}`);
            setActiveModule('groups');
          }}
        />
      ) : activeModule === 'influencer-imports' && auth.user.role === 'admin' ? (
        <InfluencerImportConsole onToast={showToast} />
      ) : (
        <AdminShell
          mobileView={!activeFriendId || mobileListOpen ? 'list' : 'chat'}
          profileOpen={mobileProfileOpen && Boolean(activeConversation)}
          sidebar={
            <ConversationList
              conversations={conversations}
              activeFriendId={activeFriendId}
              loading={conversationsLoading}
              searchTerm={searchTerm}
              debouncedSearchTerm={debouncedSearchTerm}
              scope={scope}
              pagination={pagination}
              onSearchChange={setSearchTerm}
              onScopeChange={setScope}
              onPreviousPage={() => void goToPreviousPage()}
              onNextPage={() => void goToNextPage()}
              onSelect={handleSelectConversation}
              onRefresh={() => refreshConversations({ toast: showToast })}
              statText={statText}
            />
          }
          chat={
            <ChatPanel
              conversation={activeConversation}
              messages={messages}
              messageFeedback={messageFeedback}
              quality={quality}
              loading={messagesLoading}
              sending={sending}
              conversationAiCost={conversationAiCost}
              hasMoreMessages={hasMoreMessages}
              onLoadOlderMessages={(friendId) => { void loadOlderMessages(friendId); }}
              onModeToggle={handleModeToggle}
              onQualityCheck={handleQualityCheck}
              onSendReply={handleSendReply}
              onSaveMessageFeedback={handleSaveMessageFeedbackWrapped}
              onRefreshMessages={handleRefreshActiveConversation}
              onShowList={() => setMobileListOpen(true)}
              onOpenProfile={() => setMobileProfileOpen(true)}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          }
          profile={
            <ProfilePanel
              conversation={activeConversation}
              messages={messages}
              quality={quality}
              conversationFeedback={conversationFeedback}
              onSaveConversationFeedback={handleSaveConversationFeedbackWrapped}
              onRunProfileRefinery={handleRunProfileRefinery}
              onResolveProfileMemoryWarnings={handleResolveProfileMemoryWarnings}
              conversationAiCost={conversationAiCost}
              onClose={() => setMobileProfileOpen(false)}
            />
          }
        />
      )}
        </div>
      </div>
      <EnvironmentSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={showToast}
      />
      <div className="toast-stack">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </>
  );
}

export default App;
